import * as THREE from 'three';
import { getActiveCamera } from './active-camera';
import type { Camera, Object3D, WebGLRenderer } from 'three';

export class SceneMovementManager {
  sceneRoot: Object3D | null = null;
  camera: Camera | null = null;
  renderer: WebGLRenderer | null = null;
  private _dragging = false;
  private _pointerId: number | null = null;
  private _plane = new THREE.Plane();
  private _lastPoint = new THREE.Vector3();
  private _newPoint = new THREE.Vector3();
  private _delta = new THREE.Vector3();
  private _planeNormal = new THREE.Vector3();
  private _camRight = new THREE.Vector3();
  private _camUp = new THREE.Vector3();
  private _groundBack = new THREE.Vector3();
  private _worldUp = new THREE.Vector3(0, 1, 0);

  scaleFactor = 0.5;
  minScale = new THREE.Vector3(0.2, 0.2, 0.2);
  maxScale = new THREE.Vector3(3, 3, 3);
  private _pinching = false;
  private _pinchStartDistance = 0;
  private _pinchStartScale = new THREE.Vector3();
  private _pinchStartAngle = 0;
  private _pinchStartQuaternion = new THREE.Quaternion();

  rotationFactor = 1.5;

  constructor() {
    this.onSceneDragStarted = this.onSceneDragStarted.bind(this);
    this.onSceneDragMoved = this.onSceneDragMoved.bind(this);
    this.onSceneDragEnded = this.onSceneDragEnded.bind(this);
  }

  init(sceneRoot: Object3D, camera: Camera, renderer: WebGLRenderer | null = null) {
    this.sceneRoot = sceneRoot;
    this.camera = camera;
    this.renderer = renderer;
  }

  onSceneDragStarted(
    event: PointerEvent,
    hit: THREE.Intersection,
    raycaster: THREE.Raycaster
  ) {
    if (!hit || !this.sceneRoot) return;

    event.preventDefault();
    event.stopPropagation();

    this._pointerId = event.pointerId;
    this._dragging = true;

    this.camera = getActiveCamera(this.renderer, this.camera!);

    this.camera.getWorldDirection(this._planeNormal);
    this._plane.setFromNormalAndCoplanarPoint(this._planeNormal, hit.point);

    const ray = raycaster.ray;

    if (ray.intersectPlane(this._plane, this._lastPoint) === null) {
      this._dragging = false;
    }
  }

  onSceneDragMoved(raycaster: THREE.Raycaster) {
    if (!this._dragging || !this.sceneRoot) {
      return;
    }

    this.camera = getActiveCamera(this.renderer, this.camera!);
    this.camera.getWorldDirection(this._planeNormal);
    this._plane.setFromNormalAndCoplanarPoint(this._planeNormal, this._lastPoint);

    const ray = raycaster.ray;
    if (ray.intersectPlane(this._plane, this._newPoint) === null) return;

    this._delta.subVectors(this._newPoint, this._lastPoint);

    this._camRight.setFromMatrixColumn(this.camera.matrixWorld, 0).normalize();
    this._camUp.setFromMatrixColumn(this.camera.matrixWorld, 1).normalize();
    const h = this._delta.dot(this._camRight);
    const v = this._delta.dot(this._camUp);

    this._groundBack.copy(this._planeNormal).projectOnPlane(this._worldUp);
    if (this._groundBack.lengthSq() < 1e-10) {
      this._groundBack.set(0, 0, 0);
    } else {
      this._groundBack.normalize();
    }

    this._delta.copy(this._camRight).multiplyScalar(h);
    this._delta.addScaledVector(this._groundBack, 2 * v);

    this.sceneRoot.position.add(this._delta);
    this._lastPoint.copy(this._newPoint);
  }

  onSceneDragEnded(event: PointerEvent) {
    if (event.pointerId !== this._pointerId) return;
    this._dragging = false;
    this._pointerId = null;
  }

  _screenDistanceBetweenPointerEvents(a: PointerEvent, b: PointerEvent) {
    return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
  }

  _screenAngleBetweenPointerEvents(a: PointerEvent, b: PointerEvent) {
    return Math.atan2(b.clientY - a.clientY, b.clientX - a.clientX);
  }

  _shortestAngleDelta(fromRad: number, toRad: number) {
    let d = toRad - fromRad;
    while (d > Math.PI) d -= 2 * Math.PI;
    while (d < -Math.PI) d += 2 * Math.PI;
    return d;
  }

  _captureTwoFingerGestureStart(pts: PointerEvent[]) {
    const a = pts[0];
    const b = pts[1];
    this._pinchStartDistance = this._screenDistanceBetweenPointerEvents(a, b);
    this._pinchStartScale.copy(this.sceneRoot!.scale);
    this._pinchStartAngle = this._screenAngleBetweenPointerEvents(a, b);
    this._pinchStartQuaternion.copy(this.sceneRoot!.quaternion);
  }

  _applyTwoFingerPinchScale(pts: PointerEvent[]) {
    if (this._pinchStartDistance < 1e-6) return;
    const dist = this._screenDistanceBetweenPointerEvents(pts[0], pts[1]);
    const raw = dist / this._pinchStartDistance;
    const ratio = 1 + (raw - 1) * this.scaleFactor;
    const newScale = this._pinchStartScale.clone().multiplyScalar(ratio);
    newScale.clamp(this.minScale, this.maxScale);
    this.sceneRoot!.scale.copy(newScale);
  }

  _applyTwoFingerPinchRotation(pts: PointerEvent[]) {
    const delta =
      this._shortestAngleDelta(
        this._pinchStartAngle,
        this._screenAngleBetweenPointerEvents(pts[0], pts[1])
      ) * this.rotationFactor;
    this.sceneRoot!.quaternion.copy(this._pinchStartQuaternion);
    this.sceneRoot!.rotateOnWorldAxis(this._worldUp, -delta);
  }

  onTwoPointersDown(pts: PointerEvent[]) {
    if (!this.sceneRoot || pts.length < 2) return;

    const dist = this._screenDistanceBetweenPointerEvents(pts[0], pts[1]);
    if (dist < 1e-3) return;

    this._pinching = true;
    this._dragging = false;
    this._pointerId = null;

    this._captureTwoFingerGestureStart(pts);
  }

  onTwoPointersMove(pts: PointerEvent[]) {
    if (!this._pinching || !this.sceneRoot || pts.length < 2) return;

    this._applyTwoFingerPinchRotation(pts);
    this._applyTwoFingerPinchScale(pts);
  }

  endPinchIfNeeded(activePointerCount: number) {
    if (activePointerCount < 2) {
      this._pinching = false;
    }
  }
}

export const sceneMovementManager = new SceneMovementManager();
